import React, { useCallback, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  Checkbox,
  Menu,
  MenuItem,
} from "@mui/material";
import Label from "../../../../components/Label/Label";
import { useDispatch, useSelector } from "react-redux";
import BatchDetailsDrawer from "./BatchDetailsDrawer";
import { fetchBatchMetaData } from "../../../../redux/batches/actions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Loader from "../../../../components/Loader/loader";

const STATUS_COLORS = {
  approved: "#C9F6CF",
  rejected: "#FDD8D8",
  warning: "#FFF4D5",
  success: "#C9F6CF",
  error: "#FDD8D8",
  default: "#E0E0E0",
  completed: "#C9F6CF",
};

const bgByStatus = (key) =>
  STATUS_COLORS[String(key || "").toLowerCase()] || STATUS_COLORS.default;

const capitalizeFirst = (str = "") =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

const StatusChip = ({ label }) => (
  <Chip
    label={capitalizeFirst(label)}
    size="small"
    sx={{
      backgroundColor: bgByStatus(label),
      fontSize: "12px",
      fontWeight: 500,
    }}
  />
);

const NoResults = ({ message = "No batches found for selected filters." }) => (
  <Box py={6} textAlign="center" display={'flex'} alignItems={'center'} justifyContent={'center'} height={'calc(100vh - 350px)'}>
    {/* <Typography variant="body2">{message}</Typography> */}
    <Label text={message} />
  </Box>
);

const HEADERS = [
  "", // ✅ Checkbox column
  "Batch ID",
  "Date of Manufacture",
  // "Metadata Status",
  "Batch Status",
  "Source",
  "Action",
];

const tableCellSx = {
  padding: "8px 10px",
  lineHeight: "20px",
};

const BatchesTable = React.memo(function BatchesTable({
  batchesState,
  loadingOverride = false,
  selectedBatchIds = [],
  onSelectionChange = () => { },
  handleViewDetails = () => { }
}) {
  const {
    loader,
    apiError,
    message,
    data = {},
  } = batchesState || {};
  const { results = [] } = data;

  const dispatch = useDispatch();
  const [openDrawer, setOpenDrawer] = useState(false);

  const [batchStatus, setBatchStatus] = useState();
  const [menuState, setMenuState] = useState({ anchorEl: null, rowId: null, status: null });
  const handleCloseMenu = () => setMenuState({ anchorEl: null, rowId: null, status: null });
  const loading = loadingOverride || loader;
  const { product, site, unitOfOperation, selectedChart, selectedTab } =
    useSelector((state) => state.shared?.productSitesUopValues || {});
  const allIds = useMemo(
    () => results.map((r) => r?.batch_id).filter(Boolean),
    [results]
  );
  const allSelected =
    allIds.length > 0 && selectedBatchIds.length === allIds.length;
  const isIndeterminate =
    selectedBatchIds.length > 0 && selectedBatchIds.length < allIds.length;

  const handleBatchClick = useCallback(
    (row) => {
      if (!row.batch_id) return;
      setBatchStatus(row.batch_status);
      dispatch(
        fetchBatchMetaData({
          batch_id: row.batch_id,
          unit_op_id: unitOfOperation?.id,
        })
      );
      setOpenDrawer(true);
      handleCloseMenu();
    },
    [dispatch, unitOfOperation?.id]
  );

  const toggleSelect = useCallback(
    (batchId) => {
      if (!batchId) return;
      if (selectedBatchIds.includes(batchId)) {
        onSelectionChange(selectedBatchIds.filter((id) => id !== batchId));
      } else {
        onSelectionChange([...selectedBatchIds, batchId]);
      }
    },
    [onSelectionChange, selectedBatchIds]
  );

  const selectAll = useCallback(
    (checked) => {
      if (checked) {
        onSelectionChange(allIds);
      } else {
        onSelectionChange([]);
      }
    },
    [onSelectionChange, allIds]
  );

  const handleClick = (event, row) => {
    setBatchStatus(row.batch_status);
    setMenuState({ anchorEl: event.currentTarget, rowId: row.batch_id });
  };

  // if (loading) {
  //   return (
  //     <Box py={6} display="flex" justifyContent="center">
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  if (apiError) {
    return (
      <Box py={6} display="flex" justifyContent="center">
        <Typography color="error">Error: {message}</Typography>
      </Box>
    );
  }

  // if (!results.length) {
  //   return <NoResults />;
  // }

  return (
    <Box
      width="100%"
    >
      <TableContainer component={Paper} sx={{ borderRadius: "8px", maxHeight: "calc(100vh - 292px)", scrollbarGutter: "stable", overflow: 'auto', boxShadow: 'none' }}>
        <Table stickyHeader sx={{ tableLayout: "auto", width: "100%" }}>
          <TableHead >
            <TableRow
              sx={{
                backgroundColor: "#E9E9E9",
                height: "44px",
                "& th": {
                  padding: "6px 10px",
                  lineHeight: "normal",
                  backgroundColor: "#E9E9E9",
                },
              }}
            >
              {HEADERS.map((head, idx) => (
                <TableCell key={idx}>
                  {head === "" ? (
                    <>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={isIndeterminate}
                        onChange={(e) => selectAll(e.target.checked)}
                        inputProps={{ "aria-label": "Select all batches" }}
                      />
                      {selectedBatchIds.length > 0
                        ? ` ${selectedBatchIds.length}`
                        : ""}
                    </>
                  ) : (
                    <Label text={head} fontSize="14px" fontWeight="700" />
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody
            sx={{
              "& .MuiTableRow-root": { height: 48 },
              "& .MuiTableCell-root": { py: 0.5 },
            }}>
            { results?.map((row, index) => {
              const batchId = row?.batch_id;
              return (
                <TableRow
                  key={batchId ?? index}
                  sx={{
                    height: 32, // force row height
                    "& td": { padding: "4px 8px" }, // shrink padding
                  }}
                >
                  <TableCell sx={tableCellSx}>
                    <Checkbox
                      checked={selectedBatchIds.includes(batchId)}
                      onChange={() => toggleSelect(batchId)}
                      inputProps={{ "aria-label": `Select batch ${batchId}` }}
                    />
                  </TableCell>

                  <TableCell
                    onClick={() => handleBatchClick(row)}
                    sx={{
                      cursor: "pointer",
                      "&:hover .hover-label": {
                        textDecoration: "underline",
                        textDecorationColor: "#1B72B7",
                      },
                    }}
                  >
                    <Box className="hover-label">
                      <Label text={batchId} color="#1B72B7" />
                    </Box>
                  </TableCell>

                  <TableCell sx={tableCellSx}>
                    <Label text={row?.batch_date} />
                  </TableCell>


                  {/* <TableCell sx={tableCellSx}>
                    <StatusChip label={row?.metadata_status} />
                  </TableCell> */}

                  <TableCell sx={tableCellSx}>
                    <StatusChip label={row?.batch_status} />
                  </TableCell>

                  <TableCell sx={tableCellSx}>
                    <Label text={row?.source_table} />
                  </TableCell>
                  <TableCell sx={tableCellSx}>
                    <IconButton onClick={(e) => handleClick(e, row)}>
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      anchorEl={menuState.anchorEl}
                      open={menuState.rowId === row.batch_id}
                      onClose={handleCloseMenu}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      MenuListProps={{
                        sx: {
                          padding: 0,
                          backgroundColor: "white",
                          borderRadius: 0,
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() => handleBatchClick(row)}
                      >
                        View Meta
                      </MenuItem>
                      {
                        // (data.is_one_to_many) && 
                        <MenuItem
                          onClick={() => handleViewDetails("viewdata", row)}
                        >
                          View Data
                        </MenuItem>}
                    </Menu>
                  </TableCell>
                </TableRow>
              );
            })}
            {(!loading && results?.length === 0) && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {unitOfOperation?.id ? (
                    <Label color="textSecondary" text={"No data found"} />
                  ) : (
                    <Label color="textSecondary" text={"Select Unit Operation to view Batches"} />
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {/* {loading && <Loader />} */}
      </TableContainer>
      <BatchDetailsDrawer
        open={openDrawer}
        status={batchStatus}
        onClose={() => setOpenDrawer(false)}
      />
    </Box>
  );
});

export default BatchesTable;
